import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, change, reduxForm } from 'redux-form';
import { debounce, get, isEmpty, map } from 'lodash';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { ActivityDropdown } from '../../shared-components/search-module';
import ToggleField from '../../shared-components/reduxForms/fields/ToggleField/ToggleField';
import TextField from '../../shared-components/reduxForms/fields/TextField/TextField';
import CheckboxField from '../../shared-components/reduxForms/fields/CheckboxField/CheckboxField';
import NumberField from '../../shared-components/reduxForms/fields/NumberField/NumberField';
import TextAreaField from '../../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import SelectFieldHOC from '../../shared-components/reduxForms/fields/SelectField/SelectFieldHOC';
import SelectField from '../../shared-components/reduxForms/fields/SelectField/SelectField';
import DateTime from '../../app-bookings-request/DateTime';
import withActivityDropdownHoc from '../../app-listing/activity-dropdown-hoc';
import { submitInquiryForm } from './submit/submitInquiryForm';
import { validateInquiryForm } from './validate/validateInquiryForm';
import { INQUIRY_FORM } from './constants';
import { formatPrice } from '../utils';
import Cookie from 'react-cookie';

const ActivityInput = withActivityDropdownHoc(ActivityDropdown);
const SelectFieldInput = SelectFieldHOC(SelectField);

const InquiryForm = ({
  handleSubmit,
  dispatch,
  listing,
  schedule = [{}],
  scheduleInput = [{}],
  numberOfGuests = '',
  activity,
  quoteErrors = [],
  useGrid,
  getFeeCalc,
  isAttendeesPricing,
  intl
}) => {
  const {
    parentSpaceId: spaceId = '',
    country = '',
    use_type: useType = '',
    attendee_pricing: attendeePricing = {}
  } = listing;

  const attendeeTiers = attendeePricing?.tiers || [];
  const reachedMaxDays = 26 <= schedule.length;

  const activityChangeHandler = useCallback(
    (value) => {
      dispatch(change(INQUIRY_FORM, 'tags.activity', value.activity));
    },
    [activity]
  );

  const attendeesChangeHandler = useCallback(
    (value) => {
      dispatch(change(INQUIRY_FORM, 'number_of_guests', value));
    },
    [numberOfGuests]
  );

  const error = !isEmpty(quoteErrors)
    ? map(quoteErrors, (item) => (
      <span key={item}>
        {item}
        <br />
      </span>
    ))
    : '';

  const cookieActivity = useMemo(() => Cookie.load('saved-activity'), []);

  const renderAttendeeDropdown = () => {
    if (!isAttendeesPricing) {
      return <Field
        name='number_of_guests'
        component={NumberField}
        parse={(value) => Number(value) || null}
        placeholder={intl.formatMessage({
          id: 'inquiry_booking.inquiry_form.attendees_placeholder',
          defaultMessage: 'Attendees'
        })}
      />;
    }

    const currency = get(this, 'props.listing.prices[0].currency');
    const options = attendeeTiers.map(({ min, max, value }) => {
      return {
        label: (
          <div className="flex space-between">
            <div className="attendee-min-max">
              <FormattedMessage
                id="listing.fee_calc.attendee_prices_label"
                defaultMessage="{min} - {max} people"
                values={{
                  min,
                  max
                }}
              />
            </div>
            <div className="attendee-price-per-hour">
              <FormattedMessage
                id="listing.fee_calc.attendee_prices"
                defaultMessage="+{prices}/hr"
                values={{
                  prices: formatPrice(value, currency, intl)
                }}
              />
            </div>
          </div>
        ),
        displayLabel: (
          <div className="flex space-between">
            <span>{`${min}-${max}`}</span>
          </div>
        ),
        value: min
      };
    });

    return (
      <SelectFieldInput
        id="attendee_count_input"
        options={options || []}
        input={{
          onChange: attendeesChangeHandler,
          value: numberOfGuests
        }}
        isValid
        getOptionLabel={option => option.displayLabel}
        classNamePrefix="fee_calc"
      />
    );
  };

  return (
    <form className='InquiryForm' onSubmit={handleSubmit}>
      <div
        className={classNames('event-details-wrap', {
          'ps-container': useGrid
        })}
      >
        <div
          className={classNames({
            'ps-row': useGrid
          })}
        >
          <div
            className={classNames({
              'col-xs-12': useGrid
            })}
          >
            <div className='flex space-between'>
              <div className='heavier'>
                <FormattedMessage
                  id='inquiry_booking.inquiry_form.date_and_time_label'
                  defaultMessage='Date and time *'
                />
              </div>
              <div className='flexible-hours-field'>
                <Field
                  id='hours_flexible'
                  name='flexible_hours_selected'
                  component={CheckboxField}
                  label={intl.formatMessage({
                    id: 'inquiry_booking.inquiry_form.flexible_date_time_label',
                    defaultMessage: 'My dates/times are flexible'
                  })}
                  option
                />
              </div>
            </div>
            <div className="date-time-input-wrap">
              <FieldArray
                name='scheduleInput'
                component={DateTime}
                schedule={schedule}
                scheduleInput={scheduleInput}
                optional={false}
                spaceId={spaceId}
                getFeeCalc={getFeeCalc}
                formName={INQUIRY_FORM}
                dispatch={dispatch}
                country={country}
                reachedMaxDays={reachedMaxDays}
                groupContainerClassName='flex column-sm'
                useBootstrapGrid={false}
                datePlaceholder={intl.formatMessage({
                  id: 'inquiry_booking.inquiry_form.date_placeholder',
                  defaultMessage: 'Select date'
                })}
                startTimePlaceholder={intl.formatMessage({
                  id: 'inquiry_booking.inquiry_form.start_time_placeholder',
                  defaultMessage: 'Start'
                })}
                endTimePlaceholder={intl.formatMessage({
                  id: 'inquiry_booking.inquiry_form.end_time_placeholder',
                  defaultMessage: 'End'
                })}
                multiday
                isLabel
              />
              {error && <div className='help-block text-red'>{error}</div>}
            </div>

            <div className="attendees-input-wrap">
              <div className='heavier'>
                <FormattedMessage
                  id='inquiry_booking.inquiry_form.attendees_label'
                  defaultMessage='Attendees *'
                />
              </div>
              {renderAttendeeDropdown()}
            </div>
          </div>
        </div>
      </div>

      <div className={classNames({ 'ps-container': useGrid })}>
        <div className={classNames({ 'ps-row': useGrid })}>
          <div className={classNames({ 'col-xs-12': useGrid })}>
            <div className='inquiry-page-margins inquiry-form-flex'>
              <div className='activity-input-wrap'>
                <div className='heavier'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_form.activity_label'
                    defaultMessage='What are you planning? *'
                  />
                </div>
                <div className='margin-md-top'>
                  <Field
                    name='tags'
                    component={ActivityInput}
                    listingUseType={useType}
                    getValueFromInput={activityChangeHandler}
                    excludeCustomActivities
                    bookingActivity={activity || cookieActivity}
                  />
                </div>
              </div>

              <div className='alcohol-toggle-wrap'>
                <div className='heavier'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_form.alcohol_label'
                    defaultMessage='Will alcohol be consumed? *'
                  />
                </div>
                <div className='margin-md-top'>
                  <Field
                    name='alcohol_consumed'
                    component={ToggleField}
                    firstOption={{
                      id: 'alcohol-consumed-true',
                      value: 'true',
                      label: intl.formatMessage({
                        id: 'inquiry_booking.inquiry_form.alcohol_yes',
                        defaultMessage: 'Yes'
                      })
                    }}
                    secondOption={{
                      id: 'alcohol-consumed-false',
                      value: 'false',
                      label: intl.formatMessage({
                        id: 'inquiry_booking.inquiry_form.alcohol_no',
                        defaultMessage: 'No'
                      })
                    }}
                  />
                </div>
              </div>
            </div>

            <div className='heavier'>
              <FormattedMessage
                id='inquiry_booking.inquiry_form.message_label'
                defaultMessage='Message for host *'
              />
            </div>
            <div className='margin-md-top'>
              <Field
                name='message'
                component={TextAreaField}
                rows={5}
                placeholder={intl.formatMessage({
                  id: 'inquiry_booking.inquiry_form.message_placeholder',
                  defaultMessage:
                    'Share more about yourself, describe your activity, describe how you plan to use the space, and add any questions you might have'
                })}
              />
            </div>

            <div className='inquiry-page-margins inquiry-form-flex'>
              <div className='flex-one'>
                <div className='heavier'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_form.organization_label'
                    defaultMessage='Organization'
                  />
                </div>
                <div className='margin-md-top'>
                  <Field
                    name='organization'
                    component={TextField}
                    id='organization'
                    placeholder={intl.formatMessage({
                      id: 'inquiry_booking.inquiry_form.organization_placeholder',
                      defaultMessage: 'Organization Name'
                    })}
                  />
                </div>
              </div>

              <div className='flex-one'>
                <div className='heavier'>
                  <FormattedMessage
                    id='inquiry_booking.inquiry_form.profession_label'
                    defaultMessage='Title'
                  />
                </div>
                <div className='margin-md-top'>
                  <Field
                    name='profession'
                    component={TextField}
                    id='profession'
                    placeholder={intl.formatMessage({
                      id: 'inquiry_booking.inquiry_form.profession_placeholder',
                      defaultMessage: 'Job Title'
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

const config = {
  form: INQUIRY_FORM,
  onSubmit: debounce(submitInquiryForm, 250),
  validate: validateInquiryForm
};

InquiryForm.propTypes = {
  handleSubmit: PropTypes.func,
  dispatch: PropTypes.func,
  listing: PropTypes.object,
  schedule: PropTypes.array,
  scheduleInput: PropTypes.array,
  numberOfGuests: PropTypes.string,
  activity: PropTypes.string,
  getFeeCalc: PropTypes.func,
  errorMessage: PropTypes.string,
  quoteErrors: PropTypes.array,
  useGrid: PropTypes.bool,
  isAttendeesPricing: PropTypes.bool,
  intl: intlShape
};

export default injectIntl(reduxForm(config)(InquiryForm));
